import { useEffect, useMemo, useState } from 'react';
import { siteMeta, navLinks, essencePosts, categories, hotRank } from '@/mocks/gossipData';

interface SearchItem {
  anchor: string;
  label: string;
  group: string;
}

function buildIndex(): SearchItem[] {
  const list: SearchItem[] = [];
  navLinks.forEach((n) => list.push({ anchor: `#${n.id}`, label: n.label, group: '导航分区' }));
  categories.forEach((c) => list.push({ anchor: '#topics', label: c.name, group: '话题分类' }));
  hotRank.forEach((h) => list.push({ anchor: '#hot', label: h.word, group: '热搜词条' }));
  essencePosts.forEach((p) => list.push({ anchor: '#essence', label: p.title, group: '精华盘点' }));
  return list;
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState('');

  const index = useMemo(buildIndex, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const results = keyword.trim()
    ? index.filter((it) => it.label.toLowerCase().includes(keyword.trim().toLowerCase()))
    : index.slice(0, 6);

  const closeSearch = () => {
    setSearchOpen(false);
    setKeyword('');
  };

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-40">
        <nav
          className={`transition-colors duration-300 ${
            scrolled
              ? 'border-b border-background-200/80 bg-background-50/85 backdrop-blur-md'
              : 'border-b border-transparent bg-background-50'
          }`}
        >
          <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 md:px-6">
            {/* Brand (homepage H1) */}
            <a href="#today" className="group flex shrink-0 items-center gap-2.5" aria-label="昨日吃瓜 首页">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary-500 text-background-50">
                <i className="ri-water-flash-line text-lg" />
              </span>
              <span className="flex flex-col leading-none">
                <h1 className="font-heading text-lg font-bold tracking-tight text-foreground-950">
                  昨日吃瓜
                </h1>
                <span className="mt-1 text-[11px] font-medium tracking-wide text-foreground-500">
                  {siteMeta.tagline}
                </span>
              </span>
            </a>

            {/* Desktop links */}
            <div className="hidden items-center gap-1 lg:flex">
              {navLinks.map((n) => (
                <a
                  key={n.id}
                  href={`#${n.id}`}
                  className="whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-secondary-100 hover:text-foreground-950"
                >
                  {n.label}
                </a>
              ))}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setSearchOpen(true)}
                aria-label="搜索昨日话题"
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full text-foreground-700 transition-colors hover:bg-secondary-100 hover:text-primary-600"
              >
                <i className="ri-search-line text-lg" />
              </button>
              <a
                href="#subscribe"
                className="hidden whitespace-nowrap rounded-full bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 sm:inline-flex"
              >
                订阅瓜报
              </a>
              <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-label={open ? '关闭菜单' : '展开菜单'}
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full text-foreground-800 transition-colors hover:bg-secondary-100 lg:hidden"
              >
                <i className={open ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'} />
              </button>
            </div>
          </div>

          {/* Mobile panel */}
          {open ? (
            <div className="border-t border-background-200 bg-background-50 px-4 py-3 lg:hidden">
              <div className="grid grid-cols-2 gap-1">
                {navLinks.map((n) => (
                  <a
                    key={n.id}
                    href={`#${n.id}`}
                    onClick={() => setOpen(false)}
                    className="rounded-lg px-3 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:bg-secondary-100"
                  >
                    {n.label}
                  </a>
                ))}
              </div>
              <a
                href="#subscribe"
                onClick={() => setOpen(false)}
                className="mt-3 inline-flex w-full items-center justify-center whitespace-nowrap rounded-full bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50"
              >
                订阅昨日瓜报
              </a>
            </div>
          ) : null}
        </nav>
      </header>

      {/* Search overlay */}
      {searchOpen ? (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-foreground-950/50 px-4 pt-24 backdrop-blur-sm" onClick={closeSearch}>
          <div
            className="w-full max-w-xl overflow-hidden rounded-2xl bg-background-50"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 border-b border-background-200 px-4">
              <i className="ri-search-line text-lg text-primary-600" />
              <input
                autoFocus
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索昨日话题、热搜词、盘点…"
                className="h-12 w-full bg-transparent text-sm outline-none placeholder:text-foreground-400"
                aria-label="搜索昨日话题"
              />
              <button type="button" onClick={closeSearch} aria-label="关闭搜索" className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-foreground-500 hover:bg-secondary-100">
                <i className="ri-close-line text-lg" />
              </button>
            </div>
            <div className="max-h-[60vh] overflow-auto p-3">
              {results.length ? (
                <ul className="flex flex-col gap-1">
                  {results.map((r, i) => (
                    <li key={`${r.group}-${i}`}>
                      <a
                        href={r.anchor}
                        onClick={closeSearch}
                        className="flex items-center gap-3 rounded-xl px-3 py-2.5 transition-colors hover:bg-secondary-100"
                      >
                        <span className="rounded-md bg-secondary-100 px-2 py-0.5 text-xs text-secondary-800">{r.group}</span>
                        <span className="truncate text-sm text-foreground-800">{r.label}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="px-3 py-8 text-center text-sm text-foreground-500">没有找到与「{keyword}」相关的话题，换个词试试。</p>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}